// frontend/src/components/Topbar.jsx

import React from "react";
import { useLocation } from "react-router-dom";
import { Sun, Moon, LogOut, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useCompany } from "../context/CompanyContext";

const ROUTE_LABELS = {
  "/dashboard": "Command Center",
  "/portfolio": "Portfolio Overview",
  "/companies": "Company Registry",
  "/ingestion": "Data Ingestion",
  "/ingestion/jobs": "Ingestion Jobs",
  "/business-strength-profile": "Strength Profile",
  "/business-survival-probability": "Survival Probability",
  "/bsp": "Strength Profile",
  "/fhi": "Financial Health",
  "/macro": "Macro Intelligence",
  "/daily-briefing": "Daily Briefing",
  "/risk-monitor": "Risk Monitor",
  "/reports": "Reports",
  "/billing": "Billing",
  "/settings": "Settings",
  "/team": "Team",
  "/esg": "ESG Analytics",
  "/benchmarks": "Benchmarks",
  "/covenants": "Covenants",
  "/early-warnings": "Early Warnings",
  "/variance": "Variance",
  "/unit-economics": "Unit Economics",
  "/scenarios": "Scenarios",
  "/sensitivity": "Sensitivity",
  "/goal-seek": "Goal Seek",
  "/forecasting": "Forecasting",
  "/investment-appraisal": "Investment Appraisal",
  "/cost-benefit": "Cost-Benefit",
  "/decomposition": "Decomposition",
  "/ai-analyst": "AI Analyst",
};

const TIER_COLORS = {
  free: "#3d5a7a",
  starter: "#2563eb",
  professional: "#8b5cf6",
  team: "#06b6d4",
  enterprise: "#10b981",
  institutional: "#f59e0b",
};

function getRouteLabel(pathname) {
  return ROUTE_LABELS[pathname] || ROUTE_LABELS[`/${pathname.split("/")[1]}`] || "";
}

export default function Topbar() {
  const { user, logout } = useAuth();
  const { isDark, toggle } = useTheme();
  const { selectedCompany } = useCompany();
  const { pathname } = useLocation();

  const pageLabel = getRouteLabel(pathname);
  const tier = String(user?.tier || "").toLowerCase();
  const tierColor = TIER_COLORS[tier] || "#3d5a7a";

  const initials = (user?.full_name || user?.email || "U")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

  const displayName = user?.full_name
    ? user.full_name.split(" ")[0]
    : (user?.email || "User").split("@")[0];

  return (
    <header className="topbar">
      <div className="topbar-left">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <span className="breadcrumb-root">SignalIQ</span>

          {pageLabel ? (
            <>
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-cur">{pageLabel}</span>
            </>
          ) : null}

          {selectedCompany?.name ? (
            <>
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-company">{selectedCompany.name}</span>
            </>
          ) : null}
        </nav>
      </div>

      <div className="topbar-right">
        <div className="topbar-status" title="System status">
          <span className="status-dot online" />
          <span>Live</span>
        </div>

        <button
          className="icon-btn"
          type="button"
          onClick={toggle}
          title={isDark ? "Light mode" : "Dark mode"}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? <Sun size={14} /> : <Moon size={14} />}
        </button>

        <button
          className="icon-btn"
          type="button"
          title="Notifications"
          aria-label="Notifications"
        >
          <Bell size={14} />
        </button>

        <button
          className="icon-btn"
          type="button"
          onClick={logout}
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut size={14} />
        </button>

        {user ? (
          <div className="user-chip">
            <div className="user-avatar" aria-hidden="true">
              {initials}
            </div>

            <span className="user-chip__name">{displayName}</span>

            {tier ? (
              <span
                className="user-chip__tier"
                style={{
                  "--tier-color": tierColor,
                }}
              >
                {tier}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}